import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './HighlightedTextArea.module.css';
import FloatingLabelTextArea from '../FloatingLabelTextArea/FloatingLabelTextArea';
import { CoachHighlightRange } from '../../../../Utils/aiCoach';

type HighlightedTextAreaProps = {
  label: string;
  value: string | undefined;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  readonly?: boolean;
  suspended?: boolean;
  /** Non-overlapping, sorted ranges into `value` to mark. */
  highlightRanges: CoachHighlightRange[];
};

type BackdropRect = { top: number; left: number; width: number; height: number };

/**
 * Wraps FloatingLabelTextArea with a backdrop div rendered exactly behind the
 * textarea (which gets a transparent background while highlights are active).
 * The backdrop repeats the same text with <mark> spans around coach highlight
 * ranges, mirroring the textarea's font, padding, and scroll position so the
 * marks line up with the real text. It sits behind the textarea, so it never
 * interferes with typing or selection.
 */
const HighlightedTextArea = ({
  label,
  value,
  onChange,
  onBlur,
  onFocus,
  readonly,
  suspended,
  highlightRanges,
}: HighlightedTextAreaProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const [backdropRect, setBackdropRect] = useState<BackdropRect | null>(null);

  const text = value ?? '';
  const hasHighlights = highlightRanges.length > 0;

  const segments = useMemo(() => {
    if (!hasHighlights) {
      return null;
    }

    const parts: React.ReactNode[] = [];
    let cursor = 0;

    highlightRanges.forEach((range, index) => {
      if (range.start > cursor) {
        parts.push(<span key={`plain-${index}`}>{text.slice(cursor, range.start)}</span>);
      }
      const markClass =
        range.kind === 'conflict' || range.kind === 'unsupported'
          ? styles.markConflict
          : styles.markImprove;
      parts.push(
        <mark key={`mark-${index}`} className={`${styles.mark} ${markClass}`} title={range.guidance}>
          {text.slice(range.start, range.end)}
        </mark>
      );
      cursor = range.end;
    });

    if (cursor < text.length) {
      parts.push(<span key="plain-tail">{text.slice(cursor)}</span>);
    }

    return parts;
  }, [text, highlightRanges, hasHighlights]);

  // Track the textarea's position/size (it sits below the floating label and
  // auto-resizes with content) and keep the backdrop glued to it.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !hasHighlights) {
      setBackdropRect(null);
      return;
    }

    const textarea = container.querySelector('textarea');
    if (!textarea) return;

    const measure = () => {
      setBackdropRect({
        top: textarea.offsetTop,
        left: textarea.offsetLeft,
        width: textarea.clientWidth,
        height: textarea.clientHeight,
      });
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(textarea);

    const syncScroll = () => {
      const backdrop = backdropRef.current;
      if (backdrop) {
        backdrop.scrollTop = textarea.scrollTop;
        backdrop.scrollLeft = textarea.scrollLeft;
      }
    };
    textarea.addEventListener('scroll', syncScroll);

    return () => {
      resizeObserver.disconnect();
      textarea.removeEventListener('scroll', syncScroll);
    };
  }, [hasHighlights, text]);

  return (
    <div ref={containerRef} className={styles.wrapper}>
      {segments && backdropRect && (
        <div
          ref={backdropRef}
          className={styles.backdrop}
          style={{
            top: backdropRect.top,
            left: backdropRect.left,
            width: backdropRect.width,
            height: backdropRect.height,
          }}
          aria-hidden="true"
        >
          {segments}
          {'\n'}
        </div>
      )}
      <div className={segments ? styles.transparentTextArea : undefined}>
        <FloatingLabelTextArea
          label={label}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          readonly={readonly}
          suspended={suspended}
        />
      </div>
    </div>
  );
};

export default HighlightedTextArea;
